import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BookMultiRequest from "./BookMultiRequest";
import BookSearchModal from "./BookSearchModal";
import MyRequestStatsPanel from "./MyRequestStatsPanel";
import {
  purchaseRequestBatchAction,
  purchaseRequestBatchInit
} from "../actionCreators";
import baseApi from "../../../common/api/baseApi";
import { getCartList, addToCart, deleteCartItem } from "../../cart/api/cartApi";
import {
  isCartEnabled,
  subscribeCartFeature,
  applyCartBetaValue
} from "../../../common/utils/cartFeature";
import { SUCCESS } from "../../../common/constants/commonConstant";

const REQ_TYPE_PRIVATE = "01";
const REQ_TYPE_COMMON = "02";

const emptyDraft = () => ({
  bookType: "일반도서",
  ebookStore: "교보문고",
  bookName: "",
  reqType: REQ_TYPE_PRIVATE,
  reqUrl: "",
  price: "",
  etc: ""
});

const presentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const BookMultiRequestContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [draft, setDraft] = useState(emptyDraft());
  const [books, setBooks] = useState([]);
  const [addressType, setAddressType] = useState("office");
  const [addressEtc, setAddressEtc] = useState("");

  // 배송지 미리보기 (회원정보 주소 / 최근 배송지)
  const [myAddress, setMyAddress] = useState("");
  const [recentAddress, setRecentAddress] = useState("");

  // 본인 도서 신청 목록 (통계 패널용)
  const [myBooks, setMyBooks] = useState([]);

  // 보관함 베타 게이팅 — 활성 사용자에게만 보관함 UI 노출. util setCartEnabled 은
  // import 하지 않고 여기선 읽기/구독만 (지역 setter 는 setCartEnabledState 로 구분).
  const [cartEnabled, setCartEnabledState] = useState(isCartEnabled());

  // 보관함 — 신청 페이지 상단에서 불러오기 용. 제목만 저장돼 있음.
  const [carts, setCarts] = useState([]);
  const fetchCarts = useCallback(() => {
    getCartList()
      .then(data => setCarts((data && data.carts) || []))
      .catch(() => {});
  }, []);

  // 마운트 시 한 번만 호출. token interceptor 가 자동 첨부.
  // 그리고 이전 세션의 errorReason 잔여 상태로 진입 즉시 alert 뜨는 거 방지 위해 init.
  useEffect(() => {
    dispatch(purchaseRequestBatchInit());
    baseApi
      .post("/member/myInfo")
      .then(res => {
        const myInfo = res && res.data && res.data.myInfo;
        setMyAddress((myInfo && myInfo.address) || "");
        // 보관함 베타 값(계정) hydrate — 구독 중인 게이팅 state 가 갱신됨.
        applyCartBetaValue(myInfo && myInfo.cartBetaYn);
      })
      .catch(() => {});
    baseApi
      .get("/book/recentAddress")
      .then(res => {
        const addr = (res && res.data && res.data.recentAddress) || "";
        setRecentAddress(addr);
      })
      .catch(() => {});
    // 본인 도서 신청 목록 (admin 일반 사용자 둘 다 — 백엔드 매퍼가 일반 사용자는 본인 신청만 반환).
    baseApi
      .post("/books/purchaseRequest", {})
      .then(res => {
        const list = (res && res.data && res.data.books) || [];
        setMyBooks(list);
      })
      .catch(() => {});
    // 보관함은 베타 활성 사용자만 조회. 비활성이면 carts 는 빈 배열 유지.
    if (cartEnabled) fetchCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 베타 토글 구독 — 꺼짐→켜짐 시 보관함 목록을 새로 불러오고, 켜짐→꺼짐 시 비운다.
  useEffect(() => {
    const unsubscribe = subscribeCartFeature(enabled => {
      setCartEnabledState(enabled);
      if (enabled) fetchCarts();
      else setCarts([]);
    });
    return unsubscribe;
  }, [fetchCarts]);

  // 검색 모달
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchSource, setSearchSource] = useState("yes24");
  const [searchKeyword, setSearchKeyword] = useState("");
  // 검색어 미입력 시 도서명 Input 아래 인라인 힌트 (alert 대체)
  const [searchHint, setSearchHint] = useState(null);
  // 신청 실패 사유 — 신청하기 버튼 근처 인라인 메시지 (alert 대체)
  const [submitError, setSubmitError] = useState(null);
  // 보관함 담기 완료 인라인 안내 — 2.5초 후 자동 소거
  const [keepHint, setKeepHint] = useState(null);

  // 보관함 "검색해서 신청" 으로 진입한 경우 — 제목 채우고 바로 검색 모달 오픈.
  // 새로고침 시 재오픈 방지 위해 history state 는 소비 후 정리.
  useEffect(() => {
    const keyword =
      location.state && location.state.searchKeyword
        ? String(location.state.searchKeyword).trim()
        : "";
    if (keyword) {
      // 제목만 채우고 신청구분 선택은 사용자에게 맡김 (전자책도 고를 수 있게).
      setDraft(prev => ({ ...prev, bookName: keyword }));
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!keepHint) return;
    const t = setTimeout(() => setKeepHint(null), 2500);
    return () => clearTimeout(t);
  }, [keepHint]);

  const { isRequesting, isRequested, batchNum, errorReason } = useSelector(
    state => state.bookMultiRequestReducer
  );

  const onChangeDraft = useCallback((field, value) => {
    if (field === "bookName") setSearchHint(null);
    setDraft(prev => ({ ...prev, [field]: value }));
  }, []);

  const onResetDraft = useCallback(() => {
    setDraft(emptyDraft());
  }, []);

  const onSearchBook = useCallback(() => {
    if (!draft.bookName.trim()) {
      setSearchHint("검색할 도서명을 입력하세요.");
      return;
    }
    setSearchHint(null);
    let source = "yes24";
    if (draft.bookType === "전자책") {
      source = draft.ebookStore === "교보문고" ? "kyobo" : "ridi";
    }
    setSearchSource(source);
    setSearchKeyword(draft.bookName.trim());
    setSearchModalOpen(true);
  }, [draft]);

  const onModalSelect = useCallback(picked => {
    setDraft(prev => ({
      ...prev,
      bookName: picked.bookName || prev.bookName,
      reqUrl: picked.reqUrl || prev.reqUrl,
      price: picked.price ? String(picked.price) : prev.price
    }));
  }, []);

  const onModalClose = useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  const onAddBook = useCallback(() => {
    setBooks(prev => [...prev, { ...draft }]);
    setDraft(emptyDraft());
  }, [draft]);

  const onRemoveBook = useCallback(idx => {
    setBooks(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // 보관함 저장 — 제목만 보관 (URL/가격은 변동되므로 신청 시 재검색).
  const keepTitle = useCallback((bookName, onDone) => {
    const name = (bookName || "").trim();
    if (!name) return;
    addToCart([name])
      .then(data => {
        const result = data && data.result;
        if (!result || result.retCode !== SUCCESS) {
          alert((result && result.retMsg) || "보관함 저장에 실패했습니다.");
          return;
        }
        setKeepHint(`"${name}" 을(를) 보관함에 담았습니다.`);
        fetchCarts();
        if (onDone) onDone();
      })
      .catch(err => {
        if (err && err.tokenExpired) return;
        alert("보관함 저장에 실패했습니다.");
      });
  }, [fetchCarts]);

  // 입력 폼의 도서명을 보관함으로 (다음에 신청).
  const onKeepDraft = useCallback(() => {
    keepTitle(draft.bookName, () => setDraft(emptyDraft()));
  }, [draft.bookName, keepTitle]);

  // 신청 목록 행을 보관함으로 — 제목만 저장하고 행 제거.
  const onKeepBook = useCallback(
    idx => {
      const b = books[idx];
      if (!b) return;
      keepTitle(b.bookName, () =>
        // 비동기 응답 사이에 목록이 바뀔 수 있으므로 인덱스가 아닌 객체 동일성으로 제거.
        setBooks(prev => prev.filter(x => x !== b))
      );
    },
    [books, keepTitle]
  );

  // 보관함 항목을 신청에 사용 — 제목만 저장돼 있으므로 항상 재검색해서
  // 현재 URL/가격으로 고른다. 입력 중이던 draft 는 초기화됨(사용 전 확인 팝업).
  const [confirmUseName, setConfirmUseName] = useState(null);

  const applyUseCart = useCallback(name => {
    const n = (name || "").trim();
    if (!n) return;
    // 제목만 폼에 채운다. 보관함엔 제목만 있으므로 신청구분(일반도서/전자책)은
    // 사용자가 고른 뒤 검색해야 함 (자동으로 일반도서 검색을 열면 전자책을 못 고름).
    setDraft({ ...emptyDraft(), bookName: n });
  }, []);

  const onUseCart = useCallback(
    name => {
      // 현재 입력 중인 내용이 있으면 사라진다고 안내 후 진행.
      const dirty = !!(
        draft.bookName.trim() ||
        draft.reqUrl.trim() ||
        String(draft.price).trim() ||
        (draft.etc || "").trim()
      );
      if (dirty) setConfirmUseName(name);
      else applyUseCart(name);
    },
    [draft, applyUseCart]
  );

  const onConfirmUse = useCallback(() => {
    applyUseCart(confirmUseName);
    setConfirmUseName(null);
  }, [confirmUseName, applyUseCart]);

  const onCancelUse = useCallback(() => setConfirmUseName(null), []);

  // 신청 페이지의 보관함 목록에서 항목 삭제.
  const onDeleteCart = useCallback(cartSeq => {
    deleteCartItem(cartSeq)
      .then(data => {
        const result = data && data.result;
        if (!result || result.retCode !== SUCCESS) {
          alert((result && result.retMsg) || "보관함 삭제에 실패했습니다.");
          return;
        }
        setCarts(prev => prev.filter(c => c.cartSeq !== cartSeq));
      })
      .catch(err => {
        if (err && err.tokenExpired) return;
        alert("보관함 삭제에 실패했습니다.");
      });
  }, []);

  const onChangeAddressType = useCallback(type => {
    setAddressType(type);
  }, []);

  const onChangeAddressEtc = useCallback(value => {
    setAddressEtc(value);
  }, []);

  /**
   * 화면 도서 → 백엔드 Book 모델로 매핑.
   * - 전자책 분기: 화면은 bookType: "전자책", ebookStore: "교보문고"|"리디북스"
   *   백엔드는 BOOK_TYPE 한 컬럼이라 "전자책 - 교보문고" 형식으로 합침.
   * - reqYearMonth: 현재 연월
   */
  const toServerBook = b => ({
    bookType:
      b.bookType === "전자책" ? `전자책 - ${b.ebookStore}` : "일반도서",
    bookName: b.bookName.trim(),
    reqType: b.reqType,
    reqUrl: b.reqUrl.trim(),
    price: Number(b.price) || 0,
    etc: b.etc || "",
    reqYearMonth: presentYearMonth()
  });

  const onSubmit = useCallback(() => {
    if (books.length === 0) {
      alert("추가된 도서가 없습니다.");
      return;
    }
    if (addressType === "etc" && !addressEtc.trim()) {
      alert("기타 배송지를 입력해주세요.");
      return;
    }
    setSubmitError(null);
    const payload = {
      books: books.map(toServerBook),
      addressType,
      addressEtc: addressType === "etc" ? addressEtc.trim() : ""
    };
    dispatch(purchaseRequestBatchAction(payload));
  }, [books, addressType, addressEtc, dispatch]);

  // 신청 성공 — 목록으로 이동, 홈에서 접수 안내 메시지 표시
  useEffect(() => {
    if (isRequested) {
      dispatch(purchaseRequestBatchInit());
      navigate("/", { state: { batchSubmitted: true } });
    }
  }, [isRequested, dispatch, navigate]);

  // 신청 실패 — 인라인 메시지로 표시 (alert 대체). redux 잔여 상태는 init 으로 정리.
  useEffect(() => {
    if (errorReason) {
      setSubmitError(errorReason.retMsg || "도서 신청에 실패했습니다.");
      dispatch(purchaseRequestBatchInit());
    }
  }, [errorReason, dispatch]);

  const onCancel = useCallback(() => {
    navigate("/");
  }, [navigate]);

  return (
    <>
      <BookMultiRequest
        draft={draft}
        books={books}
        addressType={addressType}
        addressEtc={addressEtc}
        myAddress={myAddress}
        recentAddress={recentAddress}
        onChangeDraft={onChangeDraft}
        onSearchBook={onSearchBook}
        searchHint={searchHint}
        onAddBook={onAddBook}
        onResetDraft={onResetDraft}
        onRemoveBook={onRemoveBook}
        onKeepDraft={onKeepDraft}
        onKeepBook={onKeepBook}
        keepHint={keepHint}
        cartEnabled={cartEnabled}
        carts={carts}
        onUseCart={onUseCart}
        onDeleteCart={onDeleteCart}
        confirmUseName={confirmUseName}
        onConfirmUse={onConfirmUse}
        onCancelUse={onCancelUse}
        onChangeAddressType={onChangeAddressType}
        onChangeAddressEtc={onChangeAddressEtc}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isSubmitting={isRequesting}
        submitError={submitError}
        statsPanel={<MyRequestStatsPanel books={myBooks} />}
      />
      <BookSearchModal
        open={searchModalOpen}
        source={searchSource}
        initialKeyword={searchKeyword}
        onSelect={onModalSelect}
        onClose={onModalClose}
      />
    </>
  );
};

export default BookMultiRequestContainer;
